import React, { useEffect, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import { Button } from '@mui/material';
import moment from 'moment';
import { authService } from '../../..';
import { httpGetAsync } from '../../../utils/HttpClient';

// One row in the Print History list. Mirrors the backend PrintHistoryEntryDTO. An empty TemplateNames list signals an
// "Existing PDFs only" session (R2g) — the frontend renders that label instead of a comma-separated template list.
interface PrintHistoryEntry {
    loanPrintId: string;
    printTime?: string;
    printedByName: string;
    templateNames: string[];
}

interface PrintHistoryDialogProps {
    applicationId: string;
    onClose: () => void;
}

export default function PrintHistoryDialog({ applicationId, onClose }: PrintHistoryDialogProps) {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [entries, setEntries] = useState<PrintHistoryEntry[]>([]);

    const DocumentsCell = (props: GridCellProps) => (
        <td style={{ lineHeight: 1.1, whiteSpace: 'normal' }}>
            {props.field ? props.dataItem[props.field] : ''}
        </td>
    );

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const bearerToken = await authService.getToken();
                const response = await httpGetAsync(`api/documentPrint/printHistory/${applicationId}`, bearerToken!);
                if (cancelled) return;
                if (!response || !response.ok) {
                    const statusDetail = response ? ` (${response.status} ${response.statusText})` : '';
                    setError(`Failed to load print history${statusDetail}.`);
                    setLoading(false);
                    return;
                }
                const data = await response.json();
                setEntries(Array.isArray(data) ? data : []);
                setLoading(false);
            } catch (e) {
                if (cancelled) return;
                const message = e instanceof Error ? e.message : String(e);
                setError(`Failed to load print history: ${message}`);
                setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [applicationId]);

    const rows = entries.map(e => ({
        ...e,
        printTimeDisplay: e.printTime ? moment.utc(e.printTime).local().format('M/D/YYYY h:mm A') : '—',
        documentsDisplay: e.templateNames.length === 0 ? 'Existing PDFs only' : e.templateNames.join(', '),
    }));

    return (
        <Dialog title="Print History" onClose={onClose} width={800} height={600}>
            {loading && (
                <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>
            )}
            {!loading && error && (
                <div style={{ padding: 24, color: '#c00', textAlign: 'center' }}>{error}</div>
            )}
            {!loading && !error && rows.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center' }}>No print history for this application.</div>
            )}
            {!loading && !error && rows.length > 0 && (
                <Grid data={rows} style={{ height: 460 }}>
                    <GridColumn field="printTimeDisplay" title="Printed On" width={180} />
                    <GridColumn field="printedByName" title="Printed By" width={180} />
                    <GridColumn field="documentsDisplay" title="Documents" cell={DocumentsCell} />
                </Grid>
            )}
            <DialogActionsBar>
                <Button variant="contained" color="inherit" onClick={onClose}>Close</Button>
            </DialogActionsBar>
        </Dialog>
    );
}
